import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useCallback } from 'react';
import { orderRepository, OrderStatus } from '@trader-dashboard/database';
import { AllBrokerRepo } from '../types/broker.types';
import { netProfit, sumNetProfit } from '../utils/netProfit';
import { useUserAccounts } from './UserAccountsContext';
// Convert Supabase order to UI order format
function toUIOrder(order) {
    return {
        _id: order.id.toString(),
        ticket: order.ticket,
        symbol: order.symbol,
        type: order.type,
        lots: order.lots,
        open_price: order.open_price,
        open_time: order.open_time,
        sl: order.sl ?? undefined,
        tp: order.tp ?? undefined,
        status: order.status,
        close_price: order.close_price ?? undefined,
        close_time: order.close_time ?? undefined,
        profit: order.profit ?? undefined,
        swap: order.swap ?? undefined,
        commission: order.commission ?? undefined,
        comment: order.comment ?? undefined,
        magic: order.magic ?? undefined,
        accountId: order.account_id,
        activity_time: order.updated_at,
        created_at: order.created_at,
        updated_at: order.updated_at,
    };
}
const OrdersContext = createContext(undefined);
export const useOrders = () => {
    const context = useContext(OrdersContext);
    if (!context) {
        throw new Error('useOrders must be used within OrdersProvider');
    }
    return context;
};
function parseOrderType(typeStr) {
    const normalized = typeStr.toLowerCase().trim();
    if (normalized === "buy")
        return 0;
    if (normalized === "sell")
        return 1;
    if (normalized === "buy limit")
        return 2;
    if (normalized === "sell limit")
        return 3;
    if (normalized === "buy stop")
        return 4;
    if (normalized === "sell stop")
        return 5;
    throw new Error(`Unknown order type: ${typeStr}`);
}
function parseDateTime(dateStr, timeStr, timezone) {
    const [year, month, day] = dateStr.split(".").map(Number);
    const [hour, minute, second] = timeStr.split(":").map(Number);
    const offsetMinutes = getTimezoneOffset(timezone, year, month - 1, day, hour, minute, second);
    const utcTimestamp = Date.UTC(year, month - 1, day, hour, minute, second) + (offsetMinutes * 60 * 1000);
    return Math.floor(utcTimestamp / 1000);
}
function getTimezoneOffset(timezone, year, month, day, hour, minute, second) {
    const utcDate = new Date(Date.UTC(year, month, day, hour, minute, second));
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
    });
    const parts = formatter.formatToParts(utcDate);
    const tzYear = parseInt(parts.find(p => p.type === 'year').value);
    const tzMonth = parseInt(parts.find(p => p.type === 'month').value) - 1;
    const tzDay = parseInt(parts.find(p => p.type === 'day').value);
    const tzHour = parseInt(parts.find(p => p.type === 'hour').value);
    const tzMinute = parseInt(parts.find(p => p.type === 'minute').value);
    const tzSecond = parseInt(parts.find(p => p.type === 'second').value);
    const tzDate = new Date(Date.UTC(tzYear, tzMonth, tzDay, tzHour, tzMinute, tzSecond));
    const offsetMs = utcDate.getTime() - tzDate.getTime();
    return Math.floor(offsetMs / (60 * 1000));
}
function parseOrderLine(line, accountId, timezone) {
    const parts = line.trim().split(/\s+/);
    if (parts.length < 11) {
        return null;
    }
    try {
        const ticket = Number(parts[0]);
        const openDate = parts[1];
        const openTime = parts[2];
        const type = parseOrderType(parts[3]);
        const lots = Number(parts[4]);
        const symbol = parts[5];
        const openPrice = Number(parts[6]);
        const sl = Number(parts[7]);
        const tp = Number(parts[8]);
        const openTimestamp = parseDateTime(openDate, openTime, timezone);
        const hasCloseTime = parts.length >= 16 && parts[9] !== "-";
        let closeTime;
        let closePrice;
        let commission = 0;
        let swap = 0;
        let profit;
        if (hasCloseTime) {
            const closeDate = parts[9];
            const closeTimeStr = parts[10];
            closeTime = parseDateTime(closeDate, closeTimeStr, timezone);
            closePrice = Number(parts[11]);
            commission = Number(parts[12]);
            swap = Number(parts[14]);
            profit = Number(parts[15]);
        }
        return {
            ticket,
            symbol,
            type,
            lots,
            open_price: openPrice,
            open_time: openTimestamp,
            sl,
            tp,
            status: hasCloseTime ? "CLOSED" : "OPEN",
            close_price: closePrice,
            close_time: closeTime,
            profit,
            swap,
            commission,
            account_id: accountId,
        };
    }
    catch (error) {
        console.error("Error parsing line:", line, error);
        return null;
    }
}
export const OrdersProvider = ({ children }) => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(undefined);
    const [hasMore, setHasMore] = useState(false);
    const [currentOffset, setCurrentOffset] = useState(0);
    const { selectedAccountId } = useUserAccounts();
    const loadOrders = useCallback(async (offset = 0, append = false) => {
        if (!selectedAccountId)
            return;
        setLoading(true);
        setError(undefined);
        try {
            const result = await orderRepository.getOrders({
                accountId: selectedAccountId,
                limit: 50,
                offset,
                orderBy: 'open_time',
                orderDirection: 'desc',
            });
            const uiOrders = result.data.map(toUIOrder);
            if (append) {
                setOrders(prev => [...prev, ...uiOrders]);
            }
            else {
                setOrders(uiOrders);
            }
            setHasMore(result.hasMore);
            setCurrentOffset(offset);
        }
        catch (err) {
            console.error('Error loading orders from Supabase:', err);
            setError(err.message || 'Failed to load orders');
        }
        finally {
            setLoading(false);
        }
    }, [selectedAccountId]);
    const loadMore = useCallback(async () => {
        if (!hasMore || loading)
            return;
        await loadOrders(currentOffset + 50, true);
    }, [hasMore, loading, currentOffset, loadOrders]);
    const refreshOrders = useCallback(async () => {
        setOrders([]);
        setCurrentOffset(0);
        setHasMore(false);
        await loadOrders(0, false);
    }, [loadOrders]);
    const importOrders = useCallback(async (pastedData, accountId, brokerId) => {
        const broker = AllBrokerRepo.getBrokerById(brokerId);
        if (!broker) {
            throw new Error(`Broker not found: ${brokerId}`);
        }
        const timezone = broker.timezone;
        const lines = pastedData.split("\n").filter((line) => line.trim());
        const errors = [];
        const startIndex = lines[0]?.toLowerCase().includes("ticket") ? 1 : 0;
        const ordersToImport = [];
        for (let i = startIndex; i < lines.length; i++) {
            const line = lines[i];
            const order = parseOrderLine(line, accountId, timezone);
            if (!order) {
                errors.push(`Line ${i + 1}: Invalid format`);
                continue;
            }
            ordersToImport.push(order);
        }
        // Mark all orders as imported so the events system knows to skip notifications
        const ordersWithSource = ordersToImport.map(order => ({
            ...order,
            source: 'import',
        }));
        try {
            const result = await orderRepository.upsertOrders(ordersWithSource);
            return {
                success: true,
                importedCount: result.length,
                errors,
            };
        }
        catch (error) {
            console.error('Error importing orders:', error);
            throw new Error(error.message || 'Failed to import orders');
        }
    }, []);
    const getSummaryReport = useCallback(async (accountId) => {
        try {
            const result = await orderRepository.getOrders({
                accountId,
                status: OrderStatus.CLOSED,
                limit: 10000,
                offset: 0,
            });
            const closedOrders = result.data.map(toUIOrder);
            const totalOrders = closedOrders.length;
            const totalProfit = sumNetProfit(closedOrders);
            const pairMap = new Map();
            closedOrders.forEach(order => {
                const existing = pairMap.get(order.symbol) || { orders: [], profit: 0 };
                existing.orders.push(order);
                existing.profit += netProfit(order);
                pairMap.set(order.symbol, existing);
            });
            const byPair = Array.from(pairMap.entries()).map(([symbol, data]) => ({
                symbol,
                totalOrders: data.orders.length,
                totalProfit: data.profit,
                avgProfit: data.profit / data.orders.length,
            })).sort((a, b) => b.totalProfit - a.totalProfit);
            const monthMap = new Map();
            closedOrders.forEach(order => {
                if (order.close_time) {
                    const date = new Date(order.close_time * 1000);
                    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                    const existing = monthMap.get(monthKey) || { orders: [], profit: 0 };
                    existing.orders.push(order);
                    existing.profit += netProfit(order);
                    monthMap.set(monthKey, existing);
                }
            });
            const byMonth = Array.from(monthMap.entries()).map(([month, data]) => ({
                month,
                totalOrders: data.orders.length,
                totalProfit: data.profit,
            })).sort((a, b) => a.month.localeCompare(b.month));
            return {
                totalOrders,
                totalProfit,
                byPair,
                byMonth,
            };
        }
        catch (error) {
            console.error('Error generating summary report:', error);
            throw new Error(error.message || 'Failed to generate summary report');
        }
    }, []);
    const value = {
        orders,
        loading,
        error,
        hasMore,
        loadOrders,
        loadMore,
        refreshOrders,
        importOrders,
        getSummaryReport,
    };
    return (_jsx(OrdersContext.Provider, { value: value, children: children }));
};
